import { Crosshair, Gauge, BadgeCheck, Server, Archive, ShieldCheck, CreditCard, PieChart, Banknote } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { consultNav, consultPage } from "../i18n/consult";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { Reveal, SectionHeading } from "../components/Reveal";

const serviceIcons = [Crosshair, Gauge, BadgeCheck, Server, Archive, ShieldCheck];
const regulatedIcons = [CreditCard, PieChart, Banknote];

export default function ConsultAudit() {
  const { lang } = useLang();
  const c = consultPage[lang];

  return (
    <>
      <PageHero
        testid="consult-hero"
        eyebrow={c.eyebrow}
        titleLines={c.title}
        paragraphs={[c.p1, c.p2]}
      />

      <section className="py-20 lg:py-28" data-testid="consult-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={c.servicesEyebrow} title={c.servicesTitle} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <Reveal key={s.name} delay={(i % 3) * 0.08} className="h-full">
                  <div
                    className="relative h-full rounded-lg p-7 bg-obsidian/80 border border-sovereign/15 hover:border-sovereign/50 transition-all duration-500 hover:shadow-[0_16px_50px_rgba(0,240,255,0.08)] overflow-hidden"
                    data-testid={`consult-service-${i}`}
                  >
                    <div className="scanlines absolute inset-0 pointer-events-none" aria-hidden="true" />
                    <div className="relative">
                      <div className="flex items-start justify-between">
                        <Icon className="w-6 h-6 text-sovereign" strokeWidth={1.5} aria-hidden="true" />
                        {s.tag ? (
                          <span className="border border-eeco/40 text-eeco px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase">{s.tag}</span>
                        ) : (
                          <span className="font-mono text-[10px] tracking-[0.25em] text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                        )}
                      </div>
                      <h3 className="mt-6 font-display text-lg font-bold text-slate-100">{s.name}</h3>
                      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-sovereign/10" data-testid="consult-cyber">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={c.cyberEyebrow} title={c.cyberTitle} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.frameworks.map((f, i) => (
              <Reveal key={f.k} delay={(i % 3) * 0.07} className="h-full">
                <div className="glass rounded-lg p-6 h-full hover:border-sovereign/40 transition-colors duration-400" data-testid={`consult-framework-${i}`}>
                  <p className="font-mono text-2xl font-bold text-sovereign tracking-[0.15em]">{f.k}</p>
                  <p className="mt-4 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-t border-sovereign/10" data-testid="consult-regulated">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={c.regulatedEyebrow} title={c.regulatedTitle} />
          <div className="mt-14 space-y-6">
            {c.regulated.map((r, i) => {
              const Icon = regulatedIcons[i];
              return (
                <Reveal key={r.name} delay={0.05}>
                  <div className="glass rounded-lg p-7 lg:p-9 grid lg:grid-cols-[1fr_2fr] gap-6 items-start" data-testid={`consult-regulated-${i}`}>
                    <div>
                      <Icon className="w-6 h-6 text-sovereign" strokeWidth={1.5} aria-hidden="true" />
                      <h3 className="mt-4 font-display text-lg font-bold text-slate-100 leading-snug">{r.name}</h3>
                      <p className="mt-3 inline-block border border-sovereign/25 bg-sovereign/5 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-sovereign">
                        {r.act}
                      </p>
                    </div>
                    <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{r.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand title={c.ctaTitle} text={c.p2} btn={c.ctaBtn} />
    </>
  );
}
